import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Divider } from "@mui/material";
import CommonTable from "../common/table/commonTable";
import { createHeaderWithSort } from "../common/table/columns";
import { white, bgGrayLight, darkDenium } from "../../constants/colors";
import ProgramIcon from "./ProgramIcon";

const ProgramNameCell = ({ getValue }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      bgcolor: bgGrayLight,
      px: 1.5,
      py: 0.8,
      borderRadius: "20px",
      color: darkDenium,
      fontSize: "12px",
      fontWeight: 500,
    }}
  >
    <ProgramIcon name={getValue()} />
    {getValue()}
  </Box>
);

ProgramNameCell.propTypes = {
  getValue: PropTypes.func.isRequired,
};

const CenteredCell = ({ val, loading }) => (
  <Box textAlign="center">{loading ? "-" : (val ?? "-")}</Box>
);

CenteredCell.propTypes = {
  val: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
};

const ValueCellRenderer = (loading) => {
  function Renderer({ getValue }) {
    return <CenteredCell val={getValue()} loading={loading} />;
  }
  Renderer.displayName = "ValueCellRenderer";
  Renderer.propTypes = {
    getValue: PropTypes.func.isRequired,
  };
  return Renderer;
};

const ProgramPerformanceOverview = ({ data, loading }) => {
  const columns = useMemo(
    () => [
      {
        header: createHeaderWithSort("Program Name", true),
        accessorKey: "name",
        cell: ProgramNameCell,
      },
      {
        header: (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {createHeaderWithSort("Total Opportunities", true)}
          </Box>
        ),
        accessorKey: "eligible",
        cell: ValueCellRenderer(loading),
      },
      {
        header: (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {createHeaderWithSort("Enrolled Opportunities", true)}
          </Box>
        ),
        accessorKey: "enrolled",
        cell: ValueCellRenderer(loading),
      },
    ],
    [loading],
  );

  const displayData = loading
    ? [
        { name: "Biosimilars", eligible: "", enrolled: "" },
        { name: "International Sourcing", eligible: "", enrolled: "" },
        { name: "Copay Assist", eligible: "", enrolled: "" },
        { name: "GLP-1", eligible: "", enrolled: "" },
        { name: "PAP", eligible: "", enrolled: "" },
        { name: "ECMP", eligible: "", enrolled: "" },
        { name: "Women's Health Program", eligible: "", enrolled: "" },
      ]
    : data;

  return (
    <Box sx={{ mb: 4, backgroundColor: white, borderRadius: "12px" }}>
      <Typography
        sx={{
          color: darkDenium,
          fontSize: "16px",
          fontWeight: 500,
          mb: 1,
          pt: 2,
          pb: 1,
          px: 2,
        }}
      >
        Program Performance Overview
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box sx={{ p: 2, pt: 1 }}>
        <CommonTable
          columns={columns}
          data={displayData}
          tableHeight={"auto"}
          paginate={false}
          sx={{
            "& .MuiTableHead-root .MuiTableCell-root": {
              color: "#403C6F",
              fontSize: "14px",
              fontWeight: 600,
              py: 1.25,
              backgroundColor: "#CFCEDA",
              border: "none",
            },
            "& .MuiTableBody-root .MuiTableCell-root": {
              color: "#403C6F",
              fontSize: "13px",
              borderBottom: "none !important",
              py: 1.5,
            },
            "& .MuiTableRow-root::after": {
              display: "none !important",
            },
          }}
        />
      </Box>
    </Box>
  );
};

ProgramPerformanceOverview.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      eligible: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      enrolled: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ).isRequired,
  loading: PropTypes.bool,
};

export default ProgramPerformanceOverview;
